/**
 * useClassificationMetrics Hook
 *
 * A React hook for fetching classification metrics from the API.
 * Provides statistics about transaction classification performance.
 */

import { useState, useCallback } from 'react';
import type { ClassificationMetrics } from '@/types/accounting';

export interface UseClassificationMetricsReturn {
  /** Current classification metrics */
  metrics: ClassificationMetrics | null;
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Fetch classification metrics */
  fetchMetrics: () => Promise<ClassificationMetrics>;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Hook for fetching classification metrics
 *
 * @example
 * ```typescript
 * const { metrics, loading, error, fetchMetrics } = useClassificationMetrics();
 *
 * // Fetch metrics on mount
 * useEffect(() => {
 *   fetchMetrics();
 * }, [fetchMetrics]);
 *
 * // Display automation rate
 * if (metrics) {
 *   console.log(`Automation rate: ${metrics.automationRate}%`);
 * }
 * ```
 */
export function useClassificationMetrics(): UseClassificationMetricsReturn {
  const [metrics, setMetrics] = useState<ClassificationMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch classification metrics from the API
   */
  const fetchMetrics = useCallback(async (): Promise<ClassificationMetrics> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/accounting/classification/metrics');

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to fetch metrics: ${response.statusText}`);
      }

      const data = (await response.json()) as ClassificationMetrics;
      setMetrics(data);
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load classification metrics';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    metrics,
    loading,
    error,
    fetchMetrics,
    clearError,
  };
}
